import React, { useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { parseISO, format } from 'date-fns';

import KEY_STUDENT_PROGRESS_REPORT_QUERY from 'graph/reports/key-student-progress-report-query';
import WeekRange from 'components/@common/WeekRange';
import useReportQueryParams from 'components/ReportsModule/hooks/use-report-query-params';
import ActivitiesWeekSpotCheckChart from '../charts/ActivitiesWeekSpotCheckChart';
import DetailsCard from 'components/@common/Cards/DetailsCard';
import ActiveDaysChart from '../charts/ActiveDaysChart';
import ActivitiesCompletedAndTimeSpentChart from '../../charts/ActivitiesCompletedAndTimeSpentChart';
import { getQueryVariables } from 'components/ReportsModule/utils/report-param-utils';
import AssessmentEnrichmentCards from '../../AssessmentEnrichmentCards';

const StudentOverrallCards: React.FC = () => {
  const [{ entityId, entityType, dateRange }] = useReportQueryParams();
  const { t } = useTranslation([
    'GradeLevel',
    'KeyOneHandedTypingHand',
    'Reports',
    'ReportsHelpCards',
    'ReportsHelpCommon'
  ]);

  const [fetchReport, { data, loading }] = useLazyQuery(KEY_STUDENT_PROGRESS_REPORT_QUERY, {
    fetchPolicy: 'network-only'
  });

  const {
    student,
    productCurriculumCode,
    dateLastLogin,
    curriculumProgress,
    skipJumpInEnabled,
    stopAndReviewEnabled,
    stopAndReviewWeek
  } = data?.keyStudentProgressReport || {};
  const { oneHandedTypingHand } = data?.keyStudentSettings || {};

  useEffect(() => {
    fetchReport({
      variables: {
        ...getQueryVariables({ entityId, entityType, dateRange }),
        settingsWhere: { studentId: entityId }
      }
    });
  }, [fetchReport, entityId, entityType, dateRange]);

  const firstDetailsCardSections = [
    {
      label: t('Grade'),
      helpText: t('gradeLevel', { ns: 'ReportsHelpCommon' }),
      value: student?.gradeLevel && t(student.gradeLevel, { ns: 'GradeLevel' })
    },
    {
      label: t('License'),
      helpText: t('license', { ns: 'ReportsHelpCommon' }),
      value: productCurriculumCode
    },
    {
      label: t('Last Login'),
      helpText: t('lastLogin', { ns: 'ReportsHelpCommon' }),
      value: dateLastLogin && format(parseISO(dateLastLogin), 'MM/dd/yyyy')
    },
    {
      label: t('Current Curriculum Week'),
      helpText: t('curriculumWeek', { ns: 'ReportsHelpCommon' }),
      value: curriculumProgress && (
        <WeekRange
          start={curriculumProgress.curriculumWeek}
          end={curriculumProgress.curriculumWeekTotal}
        />
      )
    }
  ];

  const secondDetailsCardSections = [
    {
      label: t('Skip First 6 Weeks'),
      helpText: t('skipJumpIn', { ns: 'ReportsHelpCards' }),
      value: skipJumpInEnabled ? t('Enabled') : t('Disabled')
    },
    {
      label: t('Stop and Review'),
      helpText: t('stopAndReview', { ns: 'ReportsHelpCards' }),
      value: stopAndReviewEnabled
        ? `${t('Enabled')} (${t('Week')} ${stopAndReviewWeek})`
        : t('Disabled')
    },
    ...(oneHandedTypingHand
      ? [
          {
            label: t('One-Handed Typing'),
            helpText: t('oneHandedTypingHelp', { ns: 'ReportsHelpCards' }),
            value: t(oneHandedTypingHand, { ns: 'KeyOneHandedTypingHand' })
          }
        ]
      : [])
  ];

  return (
    <>
      <section>
        <Grid container wrap="wrap" spacing={2}>
          <Grid item xs={6} lg={3}>
            <ActivitiesWeekSpotCheckChart {...curriculumProgress} loading={loading} />
          </Grid>
          <Grid item xs={6} lg={3}>
            <DetailsCard sections={firstDetailsCardSections} loading={loading} />
          </Grid>
          <Grid item xs={6} lg={3}>
            <DetailsCard sections={secondDetailsCardSections} loading={loading} />
          </Grid>
          <Grid item xs={6} lg={3}>
            <ActiveDaysChart
              activeDayCount={curriculumProgress?.activeDayCount ?? 0}
              dateRange={dateRange}
              loading={loading}
            />
          </Grid>
        </Grid>
      </section>
      <ActivitiesCompletedAndTimeSpentChart />
      <AssessmentEnrichmentCards />
    </>
  );
};

export default StudentOverrallCards;
